import { useEffect, useState } from 'react';
import {
  BACKGROUND_THEME_INTENSITY,
  clampIntensity,
  DEFAULT_BACKGROUND_THEME,
  resolveBackgroundTheme,
} from '../components/backgrounds/backgroundThemes';
import { getHomeAppearance, type HomeAppearance } from '../services/homeAppearance.service';

const CACHE_KEY = 'home-background-theme';

const getInitialHomeAppearance = (): HomeAppearance => {
  if (typeof window === 'undefined') {
    return {
      backgroundTheme: DEFAULT_BACKGROUND_THEME,
      backgroundThemeIntensity: BACKGROUND_THEME_INTENSITY.default,
    };
  }

  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<HomeAppearance>;
      return {
        backgroundTheme: resolveBackgroundTheme(parsed.backgroundTheme).id,
        backgroundThemeIntensity: clampIntensity(parsed.backgroundThemeIntensity),
      };
    }
  } catch {
    // Fall back to default on parse/storage failure
  }

  return {
    backgroundTheme: DEFAULT_BACKGROUND_THEME,
    backgroundThemeIntensity: BACKGROUND_THEME_INTENSITY.default,
  };
};

export const useHomeAppearance = (): HomeAppearance => {
  const [appearance, setAppearance] = useState<HomeAppearance>(getInitialHomeAppearance);

  useEffect(() => {
    let cancelled = false;

    const fetchAppearance = async () => {
      try {
        const data = await getHomeAppearance();
        if (cancelled) return;

        const sanitized: HomeAppearance = {
          backgroundTheme: resolveBackgroundTheme(data.backgroundTheme).id,
          backgroundThemeIntensity: clampIntensity(data.backgroundThemeIntensity),
        };

        setAppearance(sanitized);

        try {
          window.localStorage.setItem(CACHE_KEY, JSON.stringify(sanitized));
        } catch {
          // localStorage may be unavailable or disabled
        }
      } catch (err) {
        console.warn('Failed to load home page appearance:', err);
      }
    };

    void fetchAppearance();

    return () => {
      cancelled = true;
    };
  }, []);

  return appearance;
};
